import { useState } from 'react';
import {
  CalendarDays,
  DollarSign,
  TrendingDown,
  TrendingUp,
  FileText,
  Settings,
  History,
  Plus,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { Switch } from '../components/ui/switch.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.tsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion.tsx';
import { Badge } from '../components/ui/badge.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog.tsx';
import { Textarea } from '../components/ui/textarea.tsx';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../components/ui/chart.tsx';
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Dummy data
const transactions = [
  {
    id: 1,
    type: 'credit',
    amount: 50000,
    date: '2024-01-15',
    studentName: 'John Doe',
    description: 'Tuition Fee Payment',
  },
  {
    id: 2,
    type: 'debit',
    amount: 15000,
    date: '2024-01-16',
    studentName: 'Jane Smith',
    description: 'Scholarship Award',
  },
  {
    id: 3,
    type: 'credit',
    amount: 25000,
    date: '2024-01-17',
    studentName: 'Mike Johnson',
    description: 'Exam Fee Payment',
  },
  {
    id: 4,
    type: 'credit',
    amount: 30000,
    date: '2024-01-18',
    studentName: 'Sarah Wilson',
    description: 'Library Fee Payment',
  },
  {
    id: 5,
    type: 'debit',
    amount: 8000,
    date: '2024-01-19',
    studentName: 'Tom Brown',
    description: 'Fee Refund',
  },
];

const monthlyRevenueData = [
  { month: 'Sep', revenue: 450000, target: 500000 },
  { month: 'Oct', revenue: 520000, target: 500000 },
  { month: 'Nov', revenue: 480000, target: 500000 },
  { month: 'Dec', revenue: 380000, target: 500000 },
  { month: 'Jan', revenue: 620000, target: 600000 },
  { month: 'Feb', revenue: 580000, target: 600000 },
];

const feeCollectionData = [
  { name: 'Tuition', collected: 2100000, pending: 400000 },
  { name: 'Exam', collected: 350000, pending: 50000 },
  { name: 'Library', collected: 180000, pending: 20000 },
  { name: 'Sports', collected: 120000, pending: 30000 },
];

const paymentMethodData = [
  { name: 'Bank Transfer', value: 65, color: '#8b5cf6' },
  { name: 'Cash', value: 25, color: '#06b6d4' },
  { name: 'Card', value: 10, color: '#10b981' },
];

const fees = [
  {
    id: 1,
    session: '2023/2024',
    term: 'First Term',
    class: 'JSS 1',
    amount: 50000,
    installmentsEnabled: true,
  },
  {
    id: 2,
    session: '2023/2024',
    term: 'First Term',
    class: 'JSS 2',
    amount: 55000,
    installmentsEnabled: false,
  },
  {
    id: 3,
    session: '2023/2024',
    term: 'Second Term',
    class: 'JSS 1',
    amount: 50000,
    installmentsEnabled: true,
  },
];

const installments = [
  {
    id: 1,
    feeId: 1,
    studentName: 'John Doe',
    amount: 25000,
    dueDate: '2024-02-15',
    status: 'paid',
  },
  {
    id: 2,
    feeId: 1,
    studentName: 'Alice Green',
    amount: 25000,
    dueDate: '2024-02-15',
    status: 'pending',
  },
  {
    id: 3,
    feeId: 3,
    studentName: 'Bob White',
    amount: 25000,
    dueDate: '2024-03-15',
    status: 'pending',
  },
];

const historicalSessions = [
  {
    session: '2022/2023',
    totalOwed: 2500000,
    totalCollected: 2300000,
    balance: 200000,
    transactions: [
      {
        id: 101,
        type: 'credit',
        amount: 45000,
        date: '2023-01-10',
        studentName: 'Old Student 1',
        description: 'Tuition Payment',
      },
      {
        id: 102,
        type: 'credit',
        amount: 40000,
        date: '2023-02-15',
        studentName: 'Old Student 2',
        description: 'Exam Fee',
      },
    ],
  },
  {
    session: '2021/2022',
    totalOwed: 2200000,
    totalCollected: 2100000,
    balance: 100000,
    transactions: [
      {
        id: 201,
        type: 'credit',
        amount: 42000,
        date: '2022-01-12',
        studentName: 'Older Student 1',
        description: 'Tuition Payment',
      },
    ],
  },
];

export default function AccountsPage() {
  const [feeForm, setFeeForm] = useState({
    session: '',
    term: '',
    class: '',
    amount: '',
    installmentsEnabled: false,
  });

  const [transactionForm, setTransactionForm] = useState({
    type: 'credit',
    amount: '',
    studentName: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);

  const [feesData, setFeesData] = useState(fees);
  const [installmentsData, setInstallmentsData] = useState(installments);
  const [transactionsData, setTransactionsData] = useState(transactions);

  const currentSessionSummary = {
    totalOwed: 3500000,
    totalCollected: 2800000,
    balance: 700000,
  };

  const handleFeeSubmit = (e) => {
    e.preventDefault();
    console.log('Fee submitted:', feeForm);
    const newFee = {
      id: feesData.length + 1,
      ...feeForm,
      amount: Number.parseInt(feeForm.amount),
    };
    setFeesData([...feesData, newFee]);
    setFeeForm({
      session: '',
      term: '',
      class: '',
      amount: '',
      installmentsEnabled: false,
    });
  };

  const handleTransactionSubmit = (e) => {
    e.preventDefault();
    const newTransaction = {
      id: transactionsData.length + 1,
      ...transactionForm,
      amount: Number.parseInt(transactionForm.amount),
    };
    setTransactionsData([newTransaction, ...transactionsData]);
    setTransactionForm({
      type: 'credit',
      amount: '',
      studentName: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
    setIsTransactionDialogOpen(false);
  };

  const toggleInstallments = (feeId) => {
    setFeesData(
      feesData.map((fee) =>
        fee.id === feeId
          ? { ...fee, installmentsEnabled: !fee.installmentsEnabled }
          : fee
      )
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Summary Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Owed</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(currentSessionSummary.totalOwed)}
              </div>
              <p className="text-xs text-muted-foreground">
                Current session 2023/2024
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Collected
              </CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(currentSessionSummary.totalCollected)}
              </div>
              <p className="text-xs text-muted-foreground">
                80% collection rate
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Outstanding Balance
              </CardTitle>
              <TrendingDown className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(currentSessionSummary.balance)}
              </div>
              <p className="text-xs text-muted-foreground">
                Pending collections
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section for Financial Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Revenue Trend</CardTitle>
              <CardDescription>
                Revenue vs Target over the last 6 months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  revenue: {
                    label: 'Revenue',
                    color: '#8b5cf6',
                  },
                  target: {
                    label: 'Target',
                    color: '#06b6d4',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="var(--color-revenue)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="var(--color-target)"
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Fee Collection Status</CardTitle>
              <CardDescription>
                Collected vs Pending amounts by fee type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  collected: {
                    label: 'Collected',
                    color: '#10b981',
                  },
                  pending: {
                    label: 'Pending',
                    color: '#f59e0b',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={feeCollectionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="collected" fill="var(--color-collected)" />
                    <Bar dataKey="pending" fill="var(--color-pending)" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>
                Distribution of payment methods used
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  value: {
                    label: 'Percentage',
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentMethodData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed Interface */}
        <Tabs defaultValue="transactions" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger
              value="transactions"
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Transactions</span>
            </TabsTrigger>
            <TabsTrigger
              value="fee-setup"
              className="flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Fee Setup</span>
            </TabsTrigger>
            <TabsTrigger
              value="installments"
              className="flex items-center space-x-2"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Installments</span>
            </TabsTrigger>
            <TabsTrigger
              value="historical"
              className="flex items-center space-x-2"
            >
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">Historical</span>
            </TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Transactions</CardTitle>
                    <CardDescription>
                      All debits and credits for the current session
                    </CardDescription>
                  </div>
                  {/* Create Transaction Button with Dialog */}
                  <Dialog
                    open={isTransactionDialogOpen}
                    onOpenChange={setIsTransactionDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Transaction
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Create New Transaction</DialogTitle>
                        <DialogDescription>
                          Record a manual transaction for cash payments or other
                          adjustments.
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={handleTransactionSubmit}
                        className="space-y-4"
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="transaction-type">Type</Label>
                            <Select
                              value={transactionForm.type}
                              onValueChange={(value) =>
                                setTransactionForm({
                                  ...transactionForm,
                                  type: value,
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="credit">
                                  Credit (Payment In)
                                </SelectItem>
                                <SelectItem value="debit">
                                  Debit (Payment Out)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="transaction-amount">
                              Amount (₦)
                            </Label>
                            <Input
                              id="transaction-amount"
                              type="number"
                              placeholder="Enter amount"
                              value={transactionForm.amount}
                              onChange={(e) =>
                                setTransactionForm({
                                  ...transactionForm,
                                  amount: e.target.value,
                                })
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="student-name">Student Name</Label>
                          <Input
                            id="student-name"
                            placeholder="Enter student name"
                            value={transactionForm.studentName}
                            onChange={(e) =>
                              setTransactionForm({
                                ...transactionForm,
                                studentName: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="transaction-date">Date</Label>
                          <Input
                            id="transaction-date"
                            type="date"
                            value={transactionForm.date}
                            onChange={(e) =>
                              setTransactionForm({
                                ...transactionForm,
                                date: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="transaction-description">
                            Description
                          </Label>
                          <Textarea
                            id="transaction-description"
                            placeholder="Enter transaction description"
                            value={transactionForm.description}
                            onChange={(e) =>
                              setTransactionForm({
                                ...transactionForm,
                                description: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsTransactionDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Create Transaction
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactionsData.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>{transaction.date}</TableCell>
                          <TableCell className="font-medium">
                            {transaction.studentName}
                          </TableCell>
                          <TableCell>{transaction.description}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.type === 'credit'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className={
                                transaction.type === 'credit'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }
                            >
                              {transaction.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fee Setup Tab */}
          <TabsContent value="fee-setup" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Create New Fee</CardTitle>
                <CardDescription>
                  Set up fees for different sessions, terms, and classes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFeeSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="session">Session</Label>
                      <Select
                        value={feeForm.session}
                        onValueChange={(value) =>
                          setFeeForm({ ...feeForm, session: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select session" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2023/2024">2023/2024</SelectItem>
                          <SelectItem value="2024/2025">2024/2025</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="term">Term</Label>
                      <Select
                        value={feeForm.term}
                        onValueChange={(value) =>
                          setFeeForm({ ...feeForm, term: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select term" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="First Term">First Term</SelectItem>
                          <SelectItem value="Second Term">
                            Second Term
                          </SelectItem>
                          <SelectItem value="Third Term">Third Term</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="class">Class</Label>
                      <Select
                        value={feeForm.class}
                        onValueChange={(value) =>
                          setFeeForm({ ...feeForm, class: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="JSS 1">JSS 1</SelectItem>
                          <SelectItem value="JSS 2">JSS 2</SelectItem>
                          <SelectItem value="JSS 3">JSS 3</SelectItem>
                          <SelectItem value="SS 1">SS 1</SelectItem>
                          <SelectItem value="SS 2">SS 2</SelectItem>
                          <SelectItem value="SS 3">SS 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (₦)</Label>
                      <Input
                        id="amount"
                        type="number"
                        placeholder="Enter amount"
                        value={feeForm.amount}
                        onChange={(e) =>
                          setFeeForm({ ...feeForm, amount: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="installments"
                      checked={feeForm.installmentsEnabled}
                      onCheckedChange={(checked) =>
                        setFeeForm({ ...feeForm, installmentsEnabled: checked })
                      }
                    />
                    <Label htmlFor="installments">
                      Enable installment payments
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Create Fee
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Existing Fees</CardTitle>
                <CardDescription>Manage current fee structures</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {feesData.map((fee) => (
                    <div
                      key={fee.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="font-medium">
                          {fee.session} - {fee.term}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {fee.class}
                        </div>
                        <div className="text-lg font-semibold text-purple-600">
                          {formatCurrency(fee.amount)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={fee.installmentsEnabled}
                            onCheckedChange={() => toggleInstallments(fee.id)}
                          />
                          <Label className="text-sm">Installments</Label>
                        </div>
                        <Button variant="outline" size="sm">
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 bg-transparent"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Installments Tab */}
          <TabsContent value="installments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Installment Payments</CardTitle>
                <CardDescription>
                  Track ongoing installment payments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {installmentsData.map((installment) => (
                        <TableRow key={installment.id}>
                          <TableCell className="font-medium">
                            {installment.studentName}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(installment.amount)}
                          </TableCell>
                          <TableCell>{installment.dueDate}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                installment.status === 'paid'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className={
                                installment.status === 'paid'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }
                            >
                              {installment.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700 bg-transparent"
                            >
                              Mark as Paid
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Historical Sessions Tab */}
          <TabsContent value="historical" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Historical Sessions</CardTitle>
                <CardDescription>
                  View past session data and transactions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {historicalSessions.map((session, index) => (
                    <AccordionItem key={index} value={`session-${index}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-medium">{session.session}</span>
                          <div className="flex space-x-4 text-sm">
                            <span className="text-green-600">
                              Collected:{' '}
                              {formatCurrency(session.totalCollected)}
                            </span>
                            <span className="text-orange-600">
                              Balance: {formatCurrency(session.balance)}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-purple-600">
                                {formatCurrency(session.totalOwed)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Total Owed
                              </div>
                            </div>
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-green-600">
                                {formatCurrency(session.totalCollected)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Total Collected
                              </div>
                            </div>
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-orange-600">
                                {formatCurrency(session.balance)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Outstanding
                              </div>
                            </div>
                          </div>

                          <div className="rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Student</TableHead>
                                  <TableHead>Description</TableHead>
                                  <TableHead className="text-right">
                                    Amount
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {session.transactions.map((transaction) => (
                                  <TableRow key={transaction.id}>
                                    <TableCell>{transaction.date}</TableCell>
                                    <TableCell className="font-medium">
                                      {transaction.studentName}
                                    </TableCell>
                                    <TableCell>
                                      {transaction.description}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {formatCurrency(transaction.amount)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
